import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/** Module du programme de formation (écran `adm-formations`). */
export interface ModuleDeFormation {
  id: string;
  libelle: string;
  heures: number;
  ordre: number;
  /** Devoirs accrochés au module : tant qu'il y en a, il ne se retire pas. */
  devoirs: number;
}

export interface HorairesFormation {
  heureDebutMatin: string | null;
  heureFinMatin: string | null;
  heureDebutApresMidi: string | null;
  heureFinApresMidi: string | null;
  joursParSemaine: number | null;
}

export interface FormationParametree {
  id: string;
  code: string;
  titre: string;
  niveau: string;
  /** Niveau de la nomenclature BPF déduit du libellé ; `null` si ambigu. */
  niveauBpf: number | null;
  heures: number;
  heuresEcrits: number;
  heuresEcf: number;
  codeDiplome: string;
  rncp: string;
  seuilAbsencesDemiJournees: number;
  actif: boolean;
  /** Code d'émargement par défaut : défini ou non, jamais sa valeur. */
  codeEmargement: { defini: boolean; modifieLe: string | null };
  /** Durée théorique du cycle en années ; `null` tant qu'elle n'est pas renseignée. */
  dureeAnnees: number | null;
  horaires: HorairesFormation;
  /** Déduite des horaires par le serveur ; `null` s'ils ne sont pas saisis. */
  dureeHebdomadaire: { heures: number; minutes: number; libelle: string } | null;
  modules: ModuleDeFormation[];
  heuresModules: number;
  /** Formation livrée avec la plateforme : elle se désactive, elle ne se supprime pas. */
  socle: boolean;
  /** Une révision du programme est en cours dans « Ingénierie pédagogique » (TF-472). */
  revisionProgramme: boolean;
  rattachements: {
    dossiers: number;
    sessions: number;
    seances: number;
    contrats: number;
    besoins: number;
    total: number;
  };
}

export interface EtatFormations {
  formations: FormationParametree[];
  niveaux: string[];
}

/** Module tel que l'écran le garde : `id` absent pour un module pas encore enregistré. */
export interface ModuleSaisi {
  id?: string;
  cleTemporaire?: string;
  libelle: string;
  heures: number | '';
}

/**
 * Réglages d'une formation. Le programme (intitulé, niveau, codes, volumes,
 * blocs) se modifie dans « Ingénierie pédagogique » (`useProgrammes`).
 * `codeEmargement` : quatre chiffres pour le poser ou le remplacer, `null`
 * pour le retirer ; absent, il reste tel quel.
 */
export interface EntreeModificationFormation {
  actif?: boolean;
  dureeAnnees?: number | null;
  heureDebutMatin?: string | null;
  heureFinMatin?: string | null;
  heureDebutApresMidi?: string | null;
  heureFinApresMidi?: string | null;
  joursParSemaine?: number | null;
  codeEmargement?: string | null;
}

export interface JourCalendrierFormation {
  date: string;
  type: 'CENTRE_FORMATION' | 'ENTREPRISE' | 'EPREUVE_ECRITE' | 'ECF' | 'FERIE' | 'VACANCES';
}

const CLE = ['formations'];

export function useFormations() {
  return useQuery({ queryKey: CLE, queryFn: () => appelApi<EtatFormations>('/formations') });
}

/** Calendrier annuel validé depuis « Formations & programmes ». */
export function useCalendrierFormation(formationId: string | undefined, anneeScolaire: string) {
  return useQuery({
    queryKey: ['formations', formationId, 'calendrier', anneeScolaire],
    queryFn: () =>
      appelApi<JourCalendrierFormation[]>(
        `/formations/${encodeURIComponent(formationId!)}/calendriers/${encodeURIComponent(anneeScolaire)}`,
      ),
    enabled: Boolean(formationId),
  });
}

/**
 * Toute écriture renvoie l'inventaire complet et périme les référentiels : la
 * formation visée proposée sur un dossier, le catalogue et les sessions lisent
 * la même table, ils ne doivent pas rester sur l'ancienne liste.
 */
function useEcriture<TEntree>(requete: (entree: TEntree) => Promise<EtatFormations>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: requete,
    onSuccess: (etat) => {
      client.setQueryData(CLE, etat);
      void client.invalidateQueries({ queryKey: ['referentiels'] });
      void client.invalidateQueries({ queryKey: ['catalogue'] });
      void client.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

/** Modification partielle : l'écran n'envoie que ce qu'il change. */
export function useModifierFormation() {
  return useEcriture(({ id, ...entree }: EntreeModificationFormation & { id: string }) =>
    appelApi<EtatFormations>(`/formations/${encodeURIComponent(id)}`, {
      methode: 'PATCH',
      corps: entree,
    }),
  );
}

export function useSupprimerFormation() {
  return useEcriture((id: string) =>
    appelApi<EtatFormations>(`/formations/${encodeURIComponent(id)}`, { methode: 'DELETE' }),
  );
}
